import { useState } from 'react';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '@/services/firebase';
import { COLLECTIONS } from '@dnyaneshwari/shared';
import { useAuthStore } from '@/stores/authStore';
import {
  EnvelopeIcon, LockClosedIcon, ShieldCheckIcon, EyeIcon, EyeSlashIcon,
} from '@heroicons/react/24/outline';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { setUser } = useAuthStore();

  async function handleLogin(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const cred = await signInWithEmailAndPassword(auth, email, password);
      const adminDoc = await getDoc(doc(db, COLLECTIONS.ADMINS, cred.user.uid));
      if (!adminDoc.exists()) {
        await auth.signOut();
        setError('You do not have admin access. Ask the app owner to add you.');
        return;
      }
      setUser(cred.user, true);
    } catch (err: unknown) {
      const code = (err as { code?: string })?.code;
      if (code === 'auth/invalid-credential' || code === 'auth/wrong-password') {
        setError('Invalid email or password.');
      } else if (code === 'auth/user-not-found') {
        setError('No account found with this email.');
      } else {
        setError((err as Error).message);
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="h-screen overflow-hidden relative flex flex-col">
      {/* Full-screen background — shift image so light sky area is on the left */}
      <img
        src="/images/background.png"
        alt=""
        className="absolute inset-0 w-full h-full object-cover"
        style={{ objectPosition: 'center 30%' }}
      />
      {/* Subtle gradient: light on left (preserves sky), slight vignette on right for card contrast */}
      <div className="absolute inset-0 bg-gradient-to-r from-white/10 via-transparent to-black/10 pointer-events-none" />

      {/* Left quote overlay — sits in the bright sky zone */}
      <div className="absolute inset-0 flex items-start pt-20">
        <div className="w-1/2 flex flex-col items-center justify-center px-12">
          <p
            className="text-3xl leading-[1.7] text-center"
            style={{
              fontFamily: "'Noto Serif Devanagari', serif",
              fontWeight: 800,
              color: '#431407',
              textShadow: '0 1px 8px rgba(255,255,255,0.6), 0 2px 4px rgba(255,255,255,0.4)',
              letterSpacing: '0.01em',
            }}
          >
            मन शांत करण्याचा मार्ग<br />म्हणजे ज्ञानेश्वरीचा अभ्यास.
          </p>
          <p
            className="mt-5 text-lg text-center font-semibold"
            style={{
              fontFamily: "'Tiro Devanagari Marathi', serif",
              color: '#92400e',
              textShadow: '0 1px 4px rgba(255,255,255,0.5)',
            }}
          >– संत ज्ञानेश्वर महाराज</p>
          <div className="flex items-center gap-2 mt-3" style={{ color: '#92400e' }}>
            <div className="h-px w-12 bg-amber-800/40" />
            <span className="text-lg">❁</span>
            <div className="h-px w-12 bg-amber-800/40" />
          </div>
        </div>
      </div>

      {/* Card on the right */}
      <div className="relative z-10 flex-1 flex items-center justify-end pr-16 overflow-y-auto py-4">
        <div className="bg-white/95 backdrop-blur-sm rounded-3xl shadow-2xl p-7 w-full max-w-md my-auto">
          {/* Logo */}
          <div className="text-center mb-4">
            <img
              src="/images/Logo.png"
              alt="ज्ञानेश्वरी"
              className="w-20 mx-auto mb-2 drop-shadow"
            />
            <h1 className="text-xl font-bold text-gray-900">Admin Login</h1>
            <p className="text-gray-500 mt-0.5 text-sm">Sign in to manage content</p>
            {/* Decorative divider */}
            <div className="flex items-center justify-center gap-2 mt-3 text-amber-500/60">
              <div className="h-px w-10 bg-amber-400/50" />
              <span className="text-sm">❁</span>
              <div className="h-px w-10 bg-amber-400/50" />
            </div>
          </div>

          <form onSubmit={handleLogin} className="space-y-3">
            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Email</label>
              <div className="relative">
                <EnvelopeIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@example.com"
                  required
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100 text-sm bg-gray-50"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Password</label>
              <div className="relative">
                <LockClosedIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  className="w-full pl-10 pr-10 py-2.5 border border-gray-200 rounded-xl outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100 text-sm bg-gray-50"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeSlashIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Remember me + Forgot password */}
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                <input type="checkbox" className="rounded border-gray-300 text-amber-600 focus:ring-amber-400" />
                Remember Me
              </label>
              <button type="button" className="text-sm text-amber-600 font-medium hover:underline">
                Forgot Password?
              </button>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-red-700 text-sm">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 py-3 bg-amber-700 text-white rounded-xl font-semibold hover:bg-amber-800 disabled:opacity-50 transition-colors shadow-lg shadow-amber-700/25"
            >
              <ShieldCheckIcon className="w-5 h-5" />
              {loading ? 'Signing in…' : 'Sign In'}
            </button>
          </form>

        </div>
      </div>

      {/* Footer */}
      <div className="relative z-10 text-right pr-16 py-3">
        <p className="text-xs text-amber-900/60">
          © 2026 ज्ञानेश्वरी. All rights reserved.
        </p>
      </div>
    </div>
  );
}
